import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetchJson } from '../../../utils/cloudFunctionsAuth';
import { MailSyncHealthData, normalizeHealth } from '../utils/mailSyncModel';

const HEALTH_URL =
  'https://us-central1-web-construct-app.cloudfunctions.net/getMailSyncHealth';

// Push (Graph webhook) keeps the inbox fresh in seconds; this only has to
// notice a mailbox that has STOPPED syncing, which is a minutes-to-days event.
const POLL_MS = 5 * 60 * 1000;

// Survives the feed unmounting (the mail panel is opened and closed all day):
// a reopened panel paints the last known health at once instead of flashing
// "no problem" until the first fetch lands. The mount fetch still refreshes it.
let lastGoodHealth: MailSyncHealthData | null = null;

export interface UseMailSyncHealth {
  /** Last good health payload; null until the first successful fetch. */
  health: MailSyncHealthData | null;
  loading: boolean;
  /** Message from the most recent FAILED fetch; cleared by the next success.
   *  `health` keeps its last good value while this is set. */
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Mail sync health for the feed: fetched on mount, every 5 minutes while the
 * tab is visible, and on demand. A failed fetch never blanks the last good
 * value - a flaky network must not turn "leads@ paused" into silence.
 */
export function useMailSyncHealth(): UseMailSyncHealth {
  const [health, setHealth] = useState<MailSyncHealthData | null>(lastGoodHealth);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const lastFetchAtRef = useRef(0);

  const refresh = useCallback((): Promise<void> => {
    // Coalesce: a manual sync, the poll tick and a tab-focus refresh can land
    // together - one request answers all of them.
    if (inFlightRef.current) return inFlightRef.current;
    const run = (async () => {
      setLoading(true);
      try {
        const raw = await authFetchJson<any>(HEALTH_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const next = normalizeHealth(raw);
        if (!next) throw new Error('unexpected sync-health response');
        lastGoodHealth = next;
        if (mountedRef.current) { setHealth(next); setError(null); }
      } catch (e: any) {
        if (mountedRef.current) setError(e?.message || 'sync health unavailable');
      } finally {
        lastFetchAtRef.current = Date.now();
        inFlightRef.current = null;
        if (mountedRef.current) setLoading(false);
      }
    })();
    inFlightRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    // No polling while the tab is hidden (background tabs would wake a cloud
    // function every 5 minutes for a value nobody is looking at).
    const id = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, POLL_MS);
    // Ticks skipped while hidden mean the value can be stale on return.
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastFetchAtRef.current >= POLL_MS) void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mountedRef.current = false;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  return { health, loading, error, refresh };
}

export default useMailSyncHealth;
